import { Injectable } from '@angular/core';
import {
  DwAction, DwActionHandler, DwActionResult, DwActionState, DwActionType, DwSecurityUserService
} from '@devwareapps/devware-cap';
import { Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { AppPermissions } from '../../../meta-data/app-permissions.enum';
import { PostLoginExperienceService } from '../services/post-login-experience.service';
import { PostLoginRepositoryService } from '../services/post-login-repository.service';

@DwAction({
  keyName: 'test-emulated-acknowledgements-action',
  display: 'Test User Acknowledgements',
  actionType: DwActionType.menuAction,
  isGlobal: true,
  defaultSecurityConfig: {
    requireAuthentication: true,
    useEmulatedPermissions: false,
    permissionMeaning: AppPermissions.entSecurityCanEmulate
  }
})
@Injectable()
export class TestEmulatedAcknowledgementsActionService implements DwActionHandler {
  constructor(
    private securityUserService: DwSecurityUserService,
    private repository: PostLoginRepositoryService,
    private postLoginExperienceService: PostLoginExperienceService
  ) { }

  handleAction(actionState: DwActionState<any>): Observable<DwActionResult> {
    if (!this.isEmulatingUser()) {
      return of(this.createResult(false, 'Emulate a user before testing their acknowledgements.'));
    }

    return this.repository.getExperience(false, true).pipe(
      mergeMap(experience => {
        experience.IsEmulationTest = true;
        experience.PersistAcknowledgements = false;

        const hasAcknowledgements = !!experience.Terms
          || !!experience.Documents?.length
          || !!experience.Assignments?.length;

        if (!hasAcknowledgements) {
          return of(this.createResult(true, 'The emulated user has no pending acknowledgements.'));
        }

        return this.postLoginExperienceService.showAcknowledgements(experience, true).pipe(
          map(() => this.createResult(true))
        );
      })
    );
  }

  private isEmulatingUser(): boolean {
    const securityContext = this.securityUserService.securityState?.securityContext;
    const authenticatedUserId = securityContext?.AuthenticatedUser?.UserId;
    const applicationUserId = securityContext?.ApplicationUser?.UserId;

    return !!authenticatedUserId && !!applicationUserId && authenticatedUserId !== applicationUserId;
  }

  private createResult(success: boolean, message?: string): DwActionResult {
    const result = new DwActionResult();
    result.success = success;
    result.cancelled = !success;
    result.message = message;
    return result;
  }
}
